import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Calculator, Phone, ArrowRight, TrendingDown } from "lucide-react";
import { PageHeader } from "./fordon";
import { monthlyPayment } from "@/lib/vehicles";

const URL_SELF = "https://www.akhusbilfritid.se/verktyg/manadskostnad-husbil";

const FAQ = [
  {
    q: "Vad kostar en husbil per månad?",
    a: "En begagnad husbil på 500 000 kr med 20 % kontant och 84 månaders löptid landar på cirka 5 500–6 500 kr/månad beroende på ränta. Räkna ovan för din exakta situation.",
  },
  {
    q: "Vilken kontantinsats krävs?",
    a: "Minst 20 % är standard hos de flesta finansbolag. Har du mer eget kapital eller inbyte sänks månadskostnaden proportionellt.",
  },
  {
    q: "Hur lång löptid är vanligt?",
    a: "Vanligast är 60–96 månader. Längre löptid ger lägre månadsbetalning men högre total räntekostnad.",
  },
  {
    q: "Kan jag byta in min gamla husbil?",
    a: "Ja — inbytet dras direkt av från kontantinsatsen och sänker det belånade beloppet. Ring 070-862 97 43 eller kom förbi Stabsvägen 5 i Kalix så värderar vi.",
  },
];

export const Route = createFileRoute("/verktyg/manadskostnad-husbil")({
  head: () => ({
    meta: [
      { title: "Månadskostnad husbil – räkna din kostnad | AK Husbil & Fritid" },
      { name: "description", content: "Räkna ut månadskostnaden för din husbil eller husvagn. Fyll i pris, kontantinsats, löptid och ränta — se totalkostnaden direkt. Inbyte välkommet." },
      { name: "keywords", content: "månadskostnad husbil, kostnad husbil per månad, husbil finansiering, husbilslån, husbil billån" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Månadskostnad husbil – räkna direkt" },
      { property: "og:description", content: "Räkna ut månadskostnaden för din husbil eller husvagn. Kontantinsats, löptid & ränta." },
      { property: "og:url", content: URL_SELF },
    ],
    links: [{ rel: "canonical", href: URL_SELF }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function fmt(n: number): string {
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(n);
}

function Page() {
  const [price, setPrice] = useState(500000);
  const [down, setDown] = useState(100000);
  const [months, setMonths] = useState(84);
  const [rate, setRate] = useState(6.95);

  const monthly = useMemo(
    () => monthlyPayment(price, down, months, rate / 100),
    [price, down, months, rate],
  );
  const total = monthly * months + down;
  const totalRent = total - price;

  return (
    <>
      <PageHeader
        label="Verktyg · Räkna hemma"
        title={<>Månadskostnad <span className="font-italic-display text-accent">husbil</span>.</>}
        sub="Räkna ut vad din husbil eller husvagn kostar per månad. Vi använder samma modell (annuitet) som finansbolagen — så du får en realistisk siffra."
      />

      <section className="container-edge py-10">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8">
            <div className="flex items-center gap-2 text-accent">
              <Calculator size={18} />
              <span className="text-sm font-semibold uppercase tracking-wider">Kalkylator</span>
            </div>

            <div className="mt-6 space-y-5">
              <Field label="Pris (kr)" value={price} setValue={setPrice} min={50000} max={2500000} step={10000} />
              <Field label="Kontantinsats (kr)" value={down} setValue={setDown} min={0} max={Math.min(price, 2000000)} step={10000} suffix={`${Math.round((down / price) * 100)} %`} />
              <Field label="Löptid (månader)" value={months} setValue={setMonths} min={12} max={120} step={12} suffix={`${(months / 12).toFixed(1)} år`} />
              <Field label="Ränta (%)" value={rate} setValue={setRate} min={0} max={15} step={0.05} decimals />
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              Beräkningen är vägledande — exakt ränta beror på finansbolag, kreditvärdighet och pantens värde. Ring oss för en skarp offert.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <div className="rounded-3xl bg-primary p-8 text-primary-foreground shadow-xl">
              <p className="text-sm uppercase tracking-wider opacity-80">Din månadskostnad</p>
              <p className="mt-3 font-display text-5xl">{fmt(monthly)} kr</p>
              <p className="mt-2 text-sm opacity-80">/månad i {months} månader</p>
              <div className="mt-6 grid grid-cols-2 gap-4 border-t border-primary-foreground/20 pt-6 text-sm">
                <div>
                  <p className="opacity-70">Lånat belopp</p>
                  <p className="mt-1 font-semibold">{fmt(price - down)} kr</p>
                </div>
                <div>
                  <p className="opacity-70">Total räntekostnad</p>
                  <p className="mt-1 font-semibold">{fmt(totalRent)} kr</p>
                </div>
                <div>
                  <p className="opacity-70">Totalt att betala</p>
                  <p className="mt-1 font-semibold">{fmt(total)} kr</p>
                </div>
                <div>
                  <p className="opacity-70">Kontantinsats</p>
                  <p className="mt-1 font-semibold">{fmt(down)} kr</p>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6">
              <div className="flex items-center gap-2 text-accent">
                <TrendingDown size={18} />
                <span className="text-sm font-semibold">Så sänker du månadskostnaden</span>
              </div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                <li>• Byt in din gamla husbil eller husvagn — sänker det belånade beloppet direkt</li>
                <li>• Öka kontantinsatsen — 30 % istället för 20 % gör märkbar skillnad</li>
                <li>• Kortare löptid ger högre månadsbelopp men lägre total räntekostnad</li>
              </ul>
              <div className="mt-5 flex flex-wrap gap-2">
                <Link to="/husbilar-till-salu" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
                  Bläddra husbilar i lager <ArrowRight size={14} />
                </Link>
                <Link to="/husvagnar-till-salu" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary">
                  Se husvagnar
                </Link>
                <a href="tel:0708629743" className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:bg-secondary">
                  <Phone size={14} /> Ring för offert
                </a>
              </div>

            </div>
          </div>
        </div>
      </section>

      <section className="container-edge py-14">
        <p className="eyebrow">Vanliga frågor</p>
        <h2 className="mt-3 font-display text-3xl md:text-5xl">Månadskostnad husbil</h2>
        <div className="mt-8 divide-y divide-border rounded-3xl border border-border bg-card">
          {FAQ.map((f, i) => (
            <details key={i} className="group p-6">
              <summary className="cursor-pointer list-none font-display text-lg">{f.q}</summary>
              <p className="mt-3 text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

function Field({
  label, value, setValue, min, max, step, suffix, decimals,
}: {
  label: string; value: number; setValue: (n: number) => void;
  min: number; max: number; step: number; suffix?: string; decimals?: boolean;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-sm font-medium">{label}</span>
        <span className="text-sm text-muted-foreground">
          {decimals ? value.toFixed(2) : fmt(value)} {suffix && <span className="ml-1 text-xs text-accent">· {suffix}</span>}
        </span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="mt-2 w-full accent-primary"
      />
      <input
        type="number" min={min} max={max} step={step} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
      />
    </label>
  );
}
